import React from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { 
  Home, 
  MessageSquare, 
  AlertTriangle, 
  Calendar, 
  Building, 
  Users, 
  Wrench, 
  Package, 
  Receipt, 
  Megaphone, 
  CalendarDays, 
  BarChart3,
  HelpCircle,
  UserPlus,
  Shield,
  ClipboardCheck,
  Wallet,
  Book,
  BookOpen,
  Activity,
  Vote,
  Mail,
  FileSignature,
  ShieldAlert,
  Bell,
  FileCheck,
  FileText,
  Scale
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_syndic-app-1/artifacts/1ku8mjse_Logo%20Syndicappli_White_2022.png";

const Sidebar = ({ onNavigate }) => {
  const { user, logout } = useAuth();
  const { unreadSurveysCount, unreadNotificationsCount } = useNotifications();
  const navigate = useNavigate();

  const handleLogoClick = () => {
    logout();
    navigate('/login');
  };

  const handleNavClick = () => {
    // Call onNavigate callback to close sidebar on mobile
    if (onNavigate) {
      onNavigate();
    }
  };

  const navItems = {
    common: [
      { to: '/', icon: Home, label: 'Accueil' },
      { to: '/comptabilite', icon: Wallet, label: 'Comptabilité' },
      { to: '/messagerie', icon: MessageSquare, label: 'Messagerie' },
      { to: '/incidents-sinistres', icon: AlertTriangle, label: 'Incidents & Sinistres' },
      { to: '/assemblees', icon: Calendar, label: 'Assemblées générales' },
    ],
    prestataire: [
      { to: '/', icon: Home, label: 'Accueil' },
      { to: '/messagerie', icon: MessageSquare, label: 'Messagerie' },
      { to: '/incidents-sinistres', icon: AlertTriangle, label: 'Incidents & Sinistres' },
    ],
    sousCompte: [
      { to: '/', icon: Home, label: 'Accueil' },
      { to: '/messagerie', icon: MessageSquare, label: 'Messagerie' },
    ],
    sousCompteLocataire: [
      { to: '/incidents-sinistres', icon: AlertTriangle, label: 'Incidents & Sinistres' },
    ],
    sousCompteDelegue: [
      { to: '/assemblees', icon: Calendar, label: 'Assemblées générales' },
    ],
    coproprietaire: [
      { to: '/sous-comptes', icon: UserPlus, label: 'Sous-comptes' },
      { to: '/mes-charges', icon: Wallet, label: 'Mes Charges' },
      { to: '/assemblees', icon: Calendar, label: 'Assemblées' },
      { to: '/incidents-sinistres', icon: AlertTriangle, label: 'Incidents & Sinistres' },
    ],
    gestionnaire: [
      { to: '/immeubles', icon: Building, label: 'Mes Immeubles' },
      { to: '/coproprietaires', icon: Users, label: 'Mes Copropriétaires' },
      { to: '/equipe', icon: Users, label: 'Mon Équipe' },
      { to: '/prestataires', icon: Wrench, label: 'Mes Prestataires' },
      { to: '/visites', icon: ClipboardCheck, label: 'Visites d\'immeuble' },
      { to: '/carnet-entretien', icon: Book, label: 'Carnet d\'entretien' },
      { to: '/comptabilite', icon: Wallet, label: 'Comptabilité' },
      { to: '/approbation-depenses', icon: FileCheck, label: 'Approbation Dépenses' },
      { to: '/documents-juridiques', icon: Scale, label: 'Documents Juridiques' },
      { to: '/contrats', icon: FileSignature, label: 'Contrats fournisseurs' },
      { to: '/incidents-sinistres', icon: AlertTriangle, label: 'Incidents & Sinistres' },
      { to: '/dashboard-predictif', icon: Activity, label: 'Dashboard Prédictif' },
      { to: '/rgpd-archivage', icon: Shield, label: 'RGPD & Archivage' },
      { to: '/analytics/notifications', icon: BarChart3, label: 'Statistiques' },
    ],
    directeur_cabinet: [
      { to: '/immeubles', icon: Building, label: 'Mes Immeubles' },
      { to: '/comptabilite', icon: Wallet, label: 'Comptabilité' },
      { to: '/approbation-depenses', icon: FileCheck, label: 'Approbation Dépenses' },
      { to: '/documents-juridiques', icon: Scale, label: 'Documents Juridiques' },
      { to: '/coproprietaires', icon: Users, label: 'Mes Copropriétaires' },
      { to: '/equipe', icon: Users, label: 'Mon Équipe' },
      { to: '/prestataires', icon: Wrench, label: 'Mes Prestataires' },
      { to: '/contrats', icon: FileSignature, label: 'Contrats fournisseurs' },
      { to: '/incidents-sinistres', icon: AlertTriangle, label: 'Incidents & Sinistres' },
      { to: '/rgpd-archivage', icon: Shield, label: 'RGPD & Archivage' },
    ],
    directeur_cabinetAdmin: [
      { to: '/admin/utilisateurs', icon: Shield, label: 'Gestion Utilisateurs' },
    ],
    comptable: [
      { to: '/immeubles', icon: Building, label: 'Immeubles' },
      { to: '/coproprietaires', icon: Users, label: 'Copropriétaires' },
      { to: '/comptabilite', icon: Wallet, label: 'Comptabilité' },
      { to: '/grand-livre', icon: BookOpen, label: 'Grand Livre' },
      { to: '/approbation-depenses', icon: FileCheck, label: 'Approbation Dépenses' },
      { to: '/ocr-factures', icon: Receipt, label: 'OCR Factures' },
      { to: '/documents-juridiques', icon: Scale, label: 'Documents Juridiques' },
      { to: '/contrats', icon: FileSignature, label: 'Contrats' },
      { to: '/incidents-sinistres', icon: AlertTriangle, label: 'Incidents & Sinistres' },
    ],
    documentsJuridiques: [
      { to: '/documents-juridiques', icon: Scale, label: 'Documents Juridiques' },
    ],
    all: [
      { to: '/notifications', icon: Bell, label: 'Notifications' },
      { to: '/evenements', icon: CalendarDays, label: 'Événements' },
      { to: '/annonces', icon: Megaphone, label: 'Annonces' },
      { to: '/sondages', icon: Vote, label: 'Sondages' },
      { to: '/newsletters', icon: Mail, label: 'Newsletters' },
      // Modules et Factures masqués temporairement - à réactiver plus tard
      // { to: '/modules', icon: Package, label: 'Modules' },
      // { to: '/mes-factures', icon: Receipt, label: 'Factures' },
    ],
    sousCompteServices: [
      { to: '/notifications', icon: Bell, label: 'Notifications' },
      { to: '/evenements', icon: CalendarDays, label: 'Événements' },
      { to: '/annonces', icon: Megaphone, label: 'Annonces' },
      { to: '/sondages', icon: Vote, label: 'Sondages' },
      { to: '/newsletters', icon: Mail, label: 'Newsletters' },
    ],
    admin: [
      { to: '/admin', icon: BarChart3, label: 'Dashboard' },
      { to: '/admin/cabinets', icon: Building, label: 'Cabinets' },
      { to: '/admin/produits', icon: Package, label: 'Produits' },
      { to: '/admin/commandes', icon: Receipt, label: 'Commandes' },
      { to: '/admin/utilisateurs', icon: Shield, label: 'Utilisateurs' },
      { to: '/admin/roles', icon: Shield, label: 'Gestion des Rôles' },
      { to: '/admin/activite', icon: Activity, label: 'Tracker d\'activité' },
    ]
  };

  const renderNavItems = (items) => (
    items.map((item) => {
      // Check if this item should show a notification badge
      const isSurveys = item.to === '/sondages';
      const isNotifications = item.to === '/notifications';
      
      let badgeCount = 0;
      if (isSurveys && unreadSurveysCount > 0) {
        badgeCount = unreadSurveysCount;
      } else if (isNotifications && unreadNotificationsCount > 0) {
        badgeCount = unreadNotificationsCount;
      }
      
      const showBadge = badgeCount > 0;
      
      return (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === '/'}
          onClick={handleNavClick}
          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          className={({ isActive }) =>
            `flex items-center space-x-2.5 px-3 py-1.5 rounded-md transition-all duration-200 ${
              isActive
                ? 'bg-white/20 text-white font-medium'
                : 'text-white/80 hover:bg-white/10 hover:text-white'
            }`
          }
        >
          <div className="relative">
            <item.icon size={18} strokeWidth={1.5} />
            {showBadge && (
              <span 
                className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1 shadow-lg animate-pulse"
                data-testid={`${isSurveys ? 'surveys' : 'notifications'}-badge`}
              >
                {badgeCount > 9 ? '9+' : badgeCount}
              </span>
            )}
          </div>
          <span className="text-sm">{item.label}</span>
        </NavLink>
      );
    })
  );

  const renderSectionTitle = (title, icon = null) => (
    <div className="pt-2 pb-0.5 px-3 flex items-center gap-1">
      {icon}
      <span className="text-[10px] font-semibold text-white/50 uppercase tracking-wider">{title}</span>
    </div>
  );

  return (
    <div 
      className="w-64 h-screen flex flex-col sticky top-0"
      style={{ background: '#00D3A8' }}
      data-testid="sidebar"
    >
      {/* Logo Section */}
      <div className="px-4 py-3 flex-shrink-0">
        <img 
          src={LOGO_URL} 
          alt="SyndicAppli" 
          className="h-7 w-auto cursor-pointer hover:opacity-80 transition-opacity"
          data-testid="sidebar-logo"
          onClick={handleLogoClick}
          title="Retour à la connexion"
        />
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-2 space-y-0 overflow-y-auto min-h-0">
        {/* Prestataire specific navigation */}
        {user?.role === 'prestataire' ? (
          <>
            {renderNavItems(navItems.prestataire)}
          </>
        ) : user?.role === 'sous-compte' ? (
          <>
            {/* Sous-compte navigation */}
            {renderNavItems(navItems.sousCompte)}
            
            {/* Locataire: access to incidents */}
            {user?.sub_account_type === 'locataire' && user?.permissions?.can_view_incidents && (
              renderNavItems(navItems.sousCompteLocataire)
            )}
            
            {/* Délégué: access to assemblées */}
            {user?.sub_account_type === 'delegue' && user?.permissions?.can_view_ag && (
              renderNavItems(navItems.sousCompteDelegue)
            )}
            
            {/* Services for sous-comptes */}
            {renderSectionTitle('Services')}
            {renderNavItems(navItems.sousCompteServices)}
          </>
        ) : (
          <>
            {/* Common items */}
            {renderNavItems(navItems.common)}
            
            {/* Copropriétaire items */}
            {user?.role === 'coproprietaire' && (
              <>
                {renderSectionTitle('Mon Compte')}
                {renderNavItems(navItems.coproprietaire)}
              </>
            )}
            
            {/* Directeur de cabinet items - same as gestionnaire + user management */}
            {user?.role === 'directeur_cabinet' && (
              <>
                {renderSectionTitle('Gestion')}
                {renderNavItems(navItems.directeur_cabinet)}
                {renderSectionTitle('Administration')}
                {renderNavItems(navItems.directeur_cabinetAdmin)}
              </>
            )}
            
            {/* Comptable items */}
            {user?.role === 'comptable' && (
              <>
                {renderSectionTitle('Comptabilité')}
                {renderNavItems(navItems.comptable)}
              </>
            )}
            
            {/* Gestionnaire items */}
            {(user?.role === 'gestionnaire' || user?.role === 'admin' || user?.role === 'superadmin') && (
              <>
                {renderSectionTitle('Gestion')}
                {renderNavItems(navItems.gestionnaire)}
              </>
            )}
            
            {/* All users items (except prestataire) */}
            {renderSectionTitle('Services')}
            {renderNavItems(navItems.all)}
            
            {/* Admin items */}
            {(user?.role === 'admin' || user?.role === 'superadmin') && (
              <>
                {renderSectionTitle('Administration')}
                {renderNavItems(navItems.admin)}
              </>
            )}
          </>
        )}
      </nav>

      {/* Help Section */}
      <div className="px-2 py-2 flex-shrink-0">
        <div className="flex gap-1.5">
          <Link 
            to="/aide"
            className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 bg-white/15 hover:bg-white/25 text-white rounded-md transition-all duration-200 text-xs font-medium"
            data-testid="help-button"
            onClick={onNavigate}
          >
            <HelpCircle size={14} strokeWidth={1.5} />
            <span>Aide</span>
          </Link>
          <Link 
            to="/glossaire"
            className="flex-1 flex items-center justify-center gap-1.5 px-2 py-2 bg-white/10 hover:bg-white/20 text-white/80 hover:text-white rounded-md transition-all duration-200 text-xs"
            data-testid="glossaire-button"
            onClick={onNavigate}
          >
            <BookOpen size={14} strokeWidth={1.5} />
            <span>Glossaire</span>
          </Link>
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 py-1.5 border-t border-white/10 flex-shrink-0">
        <p className="text-white/40 text-[9px] text-center">
          SyndicAppli v1.0 © 2026
        </p>
      </div>
    </div>
  );
};

export default Sidebar;
